import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import AdminSidebar from '../../components/admin/AdminSidebar';
import AdminMobileNav from '../../components/admin/AdminMobileNav';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  Shield, Search, RefreshCw, ChevronLeft, ChevronRight, 
  User, Calendar, Activity, Filter, Clock, FileText, Download
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

// Action label mapping
const actionLabels = {
  create_user: 'Created User',
  update_user: 'Updated User',
  delete_user: 'Deleted User',
  suspend_user: 'Suspended User',
  activate_user: 'Activated User',
  verify_provider: 'Verified Provider',
  reject_provider: 'Rejected Provider',
  approve_document: 'Approved Document',
  reject_document: 'Rejected Document',
  update_booking_status: 'Updated Booking',
  cancel_booking: 'Cancelled Booking',
  refund_booking: 'Refunded Booking',
  withdrawal_completed: 'Completed Withdrawal',
  withdrawal_rejected: 'Rejected Withdrawal',
  withdrawal_processing: 'Processing Withdrawal',
  export_withdrawals: 'Exported Withdrawals',
  update_batch_status: 'Updated Batch Status',
  update_settings: 'Updated Settings',
  update_cms: 'Updated CMS',
  create_category: 'Created Category',
  update_category: 'Updated Category',
  delete_category: 'Deleted Category',
  create_promo: 'Created Promo Code',
  update_promo: 'Updated Promo Code',
  delete_promo: 'Deleted Promo Code',
  update_dispute: 'Updated Dispute',
  create_manager: 'Created Manager',
  update_manager: 'Updated Manager',
  delete_manager: 'Deleted Manager',
  reset_password: 'Reset Password',
  login: 'Logged In',
};

// Resource type labels
const resourceLabels = {
  user: 'User',
  provider: 'Provider',
  booking: 'Booking',
  withdrawal: 'Withdrawal',
  category: 'Category',
  promo_code: 'Promo Code',
  dispute: 'Dispute',
  settings: 'Settings',
  cms: 'CMS',
  manager: 'Manager',
  document: 'Document',
  batch: 'Batch',
};

// Action color mapping
const getActionColor = (action) => {
  if (action?.includes('create')) return 'bg-green-100 text-green-700';
  if (action?.includes('delete') || action?.includes('reject') || action?.includes('suspend')) return 'bg-red-100 text-red-700';
  if (action?.includes('update') || action?.includes('verify') || action?.includes('approve')) return 'bg-blue-100 text-blue-700';
  if (action?.includes('export')) return 'bg-purple-100 text-purple-700';
  return 'bg-slate-100 text-slate-700';
};

export default function AuditLogs() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // Filters
  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState('all');
  const [resourceFilter, setResourceFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  const fetchLogs = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20',
      });
      
      if (actionFilter && actionFilter !== 'all') {
        params.append('action', actionFilter);
      }
      if (resourceFilter && resourceFilter !== 'all') {
        params.append('resource_type', resourceFilter);
      }

      const response = await axios.get(`${API}/admin/audit-logs?${params.toString()}`);
      setLogs(response.data.logs || []);
      setTotalPages(response.data.pages || 1);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
    } finally {
      setIsLoading(false);
    }
  }, [page, actionFilter, resourceFilter]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/audit-logs/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch audit stats:', error);
    }
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('en-PH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (actionFilter && actionFilter !== 'all') {
        params.append('action', actionFilter);
      }
      if (resourceFilter && resourceFilter !== 'all') {
        params.append('resource_type', resourceFilter);
      }
      
      const response = await axios.get(`${API}/admin/audit-logs/export?${params.toString()}`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit_logs_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Audit logs exported successfully!');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export audit logs');
    }
  };

  // Check if user is admin only
  if (user?.role !== 'admin') {
    return (
      <div className="min-h-screen bg-stone-50 flex">
        <AdminSidebar />
        <AdminMobileNav />
        <main className="flex-1 p-4 md:p-8 md:ml-64 pt-16 md:pt-4">
          <div className="bg-red-50 text-red-700 p-4 rounded-xl">
            <p className="font-semibold">Access Denied</p>
            <p className="text-sm">Only administrators can view audit logs.</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 flex">
      <AdminSidebar />
      <AdminMobileNav />

      <main className="flex-1 p-4 md:p-8 md:ml-64 pt-16 md:pt-4">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-[Poppins] flex items-center gap-2">
              <Shield className="w-6 h-6 text-amber-500" />
              Audit Logs
            </h1>
            <p className="text-slate-500">Track admin and manager actions</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button 
              variant="outline" 
              onClick={() => setShowFilters(!showFilters)}
              data-testid="toggle-filters-btn"
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
            </Button>
            <Button variant="outline" onClick={() => { setPage(1); fetchLogs(); fetchStats(); }} data-testid="refresh-btn">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button 
              onClick={handleExport}
              className="bg-amber-500 hover:bg-amber-600 text-slate-900"
              data-testid="export-audit-logs-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                  <Activity className="w-5 h-5 text-blue-600" />
                </div>
                <span className="text-sm text-slate-500">Total Logs</span>
              </div>
              <p className="text-2xl font-bold text-slate-900">{stats.total_logs?.toLocaleString() || 0}</p>
            </div>
            
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-green-600" />
                </div>
                <span className="text-sm text-slate-500">Today</span>
              </div>
              <p className="text-2xl font-bold text-slate-900">{stats.today_logs?.toLocaleString() || 0}</p>
            </div>
            
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
                <span className="text-sm text-slate-500">This Week</span>
              </div>
              <p className="text-2xl font-bold text-slate-900">{stats.week_logs?.toLocaleString() || 0}</p>
            </div>
          </div>
        )}

        {/* Filters Panel */}
        {showFilters && (
          <div className="bg-white rounded-xl p-4 shadow-sm mb-6" data-testid="filters-panel">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Action Type</label>
                <Select value={actionFilter} onValueChange={(v) => { setActionFilter(v); setPage(1); }}>
                  <SelectTrigger data-testid="action-filter">
                    <SelectValue placeholder="All Actions" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Actions</SelectItem>
                    <SelectItem value="create_user">Create User</SelectItem>
                    <SelectItem value="update_user">Update User</SelectItem>
                    <SelectItem value="delete_user">Delete User</SelectItem>
                    <SelectItem value="verify_provider">Verify Provider</SelectItem>
                    <SelectItem value="reject_provider">Reject Provider</SelectItem>
                    <SelectItem value="update_settings">Update Settings</SelectItem>
                    <SelectItem value="create_category">Create Category</SelectItem>
                    <SelectItem value="update_category">Update Category</SelectItem>
                    <SelectItem value="create_promo">Create Promo</SelectItem>
                    <SelectItem value="update_dispute">Update Dispute</SelectItem>
                    <SelectItem value="create_manager">Create Manager</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Resource Type</label>
                <Select value={resourceFilter} onValueChange={(v) => { setResourceFilter(v); setPage(1); }}>
                  <SelectTrigger data-testid="resource-filter">
                    <SelectValue placeholder="All Resources" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Resources</SelectItem>
                    <SelectItem value="user">User</SelectItem>
                    <SelectItem value="provider">Provider</SelectItem>
                    <SelectItem value="booking">Booking</SelectItem>
                    <SelectItem value="withdrawal">Withdrawal</SelectItem>
                    <SelectItem value="category">Category</SelectItem>
                    <SelectItem value="promo_code">Promo Code</SelectItem>
                    <SelectItem value="dispute">Dispute</SelectItem>
                    <SelectItem value="settings">Settings</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-end">
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => { setActionFilter('all'); setResourceFilter('all'); setPage(1); }}
                  data-testid="clear-filters-btn"
                >
                  Clear Filters
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Logs Table */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="p-4 border-b border-slate-100 flex items-center justify-between">
            <h2 className="font-semibold text-slate-900">Activity Log</h2>
            <span className="text-sm text-slate-500">{total} total entries</span>
          </div>
          
          {isLoading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 mx-auto"></div>
            </div>
          ) : logs.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              <FileText className="w-12 h-12 mx-auto mb-2 text-slate-300" />
              <p>No audit logs found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="audit-logs-table">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left p-4 text-sm font-medium text-slate-600">Timestamp</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-600">User</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-600">Action</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-600">Resource</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-600">Details</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {logs.map((log, idx) => (
                    <tr key={log.id || `log-${log.timestamp}-${idx}`} className="hover:bg-slate-50" data-testid={`audit-log-row-${idx}`}>
                      <td className="p-4">
                        <span className="text-sm text-slate-600">{formatDate(log.timestamp)}</span>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center">
                            <User className="w-4 h-4 text-slate-600" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-slate-900">{log.user_email || '-'}</p>
                            <p className="text-xs text-slate-500 capitalize">{log.user_role}</p>
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getActionColor(log.action)}`}>
                          {actionLabels[log.action] || log.action}
                        </span>
                      </td>
                      <td className="p-4">
                        <div>
                          <p className="text-sm font-medium text-slate-700">{resourceLabels[log.resource_type] || log.resource_type}</p>
                          {log.resource_id && (
                            <p className="text-xs text-slate-400 font-mono">{log.resource_id.substring(0, 8)}...</p>
                          )}
                        </div>
                      </td>
                      <td className="p-4">
                        {log.details && Object.keys(log.details).length > 0 ? (
                          <div className="text-xs text-slate-600 max-w-xs truncate">
                            {Object.entries(log.details).slice(0, 2).map(([key, value]) => (
                              <span key={key} className="mr-2">
                                <span className="font-medium">{key}:</span> {String(value).substring(0, 20)}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="p-4 border-t border-slate-100 flex items-center justify-between">
              <p className="text-sm text-slate-500">
                Page {page} of {totalPages}
              </p>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  data-testid="prev-page-btn"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  data-testid="next-page-btn"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Top Actions/Users Summary */}
        {stats && (stats.top_actions?.length > 0 || stats.top_users?.length > 0) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
            {stats.top_actions?.length > 0 && (
              <div className="bg-white rounded-xl p-4 shadow-sm">
                <h3 className="font-semibold text-slate-900 mb-4">Top Actions</h3>
                <div className="space-y-3">
                  {stats.top_actions.slice(0, 5).map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getActionColor(item.action)}`}>
                        {actionLabels[item.action] || item.action}
                      </span>
                      <span className="text-sm font-medium text-slate-700">{item.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {stats.top_users?.length > 0 && (
              <div className="bg-white rounded-xl p-4 shadow-sm">
                <h3 className="font-semibold text-slate-900 mb-4">Most Active Users</h3>
                <div className="space-y-3">
                  {stats.top_users.slice(0, 5).map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center">
                          <User className="w-4 h-4 text-slate-600" />
                        </div>
                        <span className="text-sm text-slate-700">{item.email || 'Unknown'}</span>
                      </div>
                      <span className="text-sm font-medium text-slate-700">{item.count} actions</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
